import { ExternalLink, type LucideIcon } from "lucide-react";

import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CRM_LINK_STATE_LABELS, type CrmLinkState } from "@/types/customers";

interface CrmLinkBadgeProps {
  state: CrmLinkState;
  /** Override the default label (e.g. a server-provided variant). Omit to
   *  use the canonical label from `CRM_LINK_STATE_LABELS`. */
  label?: string;
  /** When provided, the badge renders as an anchor that opens the HubSpot
   *  record in a new tab. Only set this for a `linked` badge — the caller
   *  is responsible for assembling the URL (we don't know the portal id at
   *  this layer). */
  href?: string;
  /** When the state is `sync_failed`, pass the server-provided error
   *  message here to render it as a tooltip on hover. Ignored for other
   *  states (`linked`/`syncing` don't have errors to surface). */
  errorMessage?: string | null;
  /**
   * Lifecycle-stage sync sub-state, folded into this badge so a linked
   * customer shows a *single* HubSpot chip. The company link (`state`) is the
   * base; the lifecycle push only changes the badge when it's NOT settled:
   *  · `pending_*`   → badge reads as syncing (warning) — "syncing stage".
   *  · `sync_failed` → badge reads as failed (destructive) — tooltip shows
   *    `lifecycleError`.
   *  · `linked` / `unlinked` / omitted → no effect; the company link decides.
   * Pass on the customer detail header; list/summary views can omit it.
   */
  lifecycleState?: CrmLinkState;
  /** HubSpot error from the last failed lifecycle push — shown in the tooltip
   *  when `lifecycleState === "sync_failed"`. */
  lifecycleError?: string | null;
  className?: string;
}

type Tone = "ok" | "syncing" | "failed";

function lifecycleTone(lifecycle: CrmLinkState | undefined): Tone {
  switch (lifecycle) {
    case "sync_failed":
      return "failed";
    case "pending_create":
    case "pending_link":
    case "pending_update":
      return "syncing";
    default:
      // `linked` / `unlinked` / undefined — lifecycle is settled (or N/A),
      // so it doesn't override the company-link presentation.
      return "ok";
  }
}

/**
 * HubSpot linkage pill — the single chip for a customer's HubSpot connection.
 * Plain `<Badge>` text/state, no logo (the HubSpot mark itself now lives on
 * the Activity panel's header and its collapsed reopen button — see
 * `HubspotLogo`, exported from this file). Returns `null` when the customer
 * is unlinked (list views render nothing in the column).
 *
 * It folds in the lifecycle-stage sync sub-state (`lifecycleState`) so the
 * happy path shows one calm "Linked" chip; a lifecycle push that's mid-flight
 * or failed makes *this* badge reflect it (warning / destructive + tooltip)
 * instead of adding a second chip beside it.
 *
 * Tooltip precedence (most-actionable first):
 *  1. lifecycle `sync_failed` → the lifecycle HubSpot error.
 *  2. company-link `sync_failed` → the company-link error (`errorMessage`).
 *  3. lifecycle syncing → "Syncing lifecycle stage to HubSpot…".
 *  4. `linked` with `href` → "View in HubSpot" (badge is a link).
 *  5. otherwise → no tooltip.
 */
export function CrmLinkBadge({
  state,
  label,
  href,
  errorMessage,
  lifecycleState,
  lifecycleError,
  className,
}: CrmLinkBadgeProps) {
  if (state === "unlinked") return null;

  const tone = lifecycleTone(lifecycleState);
  const text = label ?? CRM_LINK_STATE_LABELS[state];

  // A lifecycle problem overrides the company-link variant so the single chip
  // signals "something needs attention" without a second badge.
  const variant: BadgeProps["variant"] =
    tone === "failed"
      ? "destructive"
      : tone === "syncing"
        ? "warning"
        : CRM_LINK_STATE_VARIANTS[state];

  // Resolve the tooltip by precedence. `null` → no tooltip wrapper.
  const tooltip: string | null =
    tone === "failed"
      ? (lifecycleError ?? "The last HubSpot lifecycle update failed.")
      : state === "sync_failed" && errorMessage
        ? errorMessage
        : tone === "syncing"
          ? "Syncing lifecycle stage to HubSpot…"
          : href
            ? "View in HubSpot"
            : null;

  // The badge renders as a HubSpot deep-link anchor only when linked AND no
  // problem state has taken over the tooltip (a failed/syncing chip shouldn't
  // also navigate away — the user needs to read the message first).
  const asLink = !!href && tone === "ok" && state === "linked";

  const inner = (
    <Badge
      variant={variant}
      className={cn(
        "gap-1 whitespace-nowrap",
        asLink && "pr-1.5 transition-opacity hover:opacity-80",
        // A tooltip-bearing, non-link badge invites hover.
        !asLink && tooltip && "cursor-help",
        className,
      )}
    >
      <span>{text}</span>
      {asLink && <ExternalLink size={10} className="opacity-70" />}
    </Badge>
  );

  if (asLink) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a href={href} target="_blank" rel="noopener noreferrer" className="no-underline">
            {inner}
          </a>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-normal break-words">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  return inner;
}

/** Badge variant per CRM link state. */
const CRM_LINK_STATE_VARIANTS: Record<CrmLinkState, BadgeProps["variant"]> = {
  unlinked: "default",
  linked: "primary",
  pending_create: "warning",
  pending_link: "warning",
  pending_update: "warning",
  sync_failed: "destructive",
};

/** The HubSpot mark — used inline in the link badge and standalone (e.g.
 *  the collapsed-panel reopen button). Default size matches the badge's
 *  original inline usage; pass `size` to scale for other contexts. */
export function HubspotLogo({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1024 1024"
      aria-hidden="true"
      className="shrink-0"
    >
      <circle cx="512" cy="512" r="512" fill="#ff7a59" />
      <path
        transform="translate(512 512) scale(1.4) translate(-512 -512)"
        d="M623.8 624.94c-38.23 0-69.24-30.67-69.24-68.51s31-68.52 69.24-68.52 69.26 30.67 69.26 68.52-31 68.51-69.26 68.51m20.74-200.42v-61a46.83 46.83 0 0 0 27.33-42.29v-1.41c0-25.78-21.32-46.86-47.35-46.86h-1.43c-26 0-47.35 21.09-47.35 46.86v1.41a46.85 46.85 0 0 0 27.33 42.29v61a135.08 135.08 0 0 0-63.86 27.79l-169.1-130.17A52.49 52.49 0 0 0 372 309c0-29.21-23.89-52.92-53.4-53s-53.45 23.59-53.48 52.81 23.85 52.88 53.36 52.93a53.29 53.29 0 0 0 26.33-7.09l166.38 128.1a132.14 132.14 0 0 0 2.07 150.3l-50.62 50.1A43.42 43.42 0 1 0 450.1 768c24.24 0 43.9-19.46 43.9-43.45a42.24 42.24 0 0 0-2-12.42l50-49.52a135.28 135.28 0 0 0 81.8 27.47c74.61 0 135.06-59.83 135.06-133.65 0-66.82-49.62-122-114.33-131.91"
        fill="#fff"
        fillRule="evenodd"
      />
    </svg>
  );
}

/**
 * `HubspotLogo` adapted to the `LucideIcon` shape, for the places that type
 * their icon slot that way — `TabRoute.icon`, `AppRoute.icon`, `EmptyState`.
 *
 * Those slots call the icon with `size` and `strokeWidth`; the mark is a
 * filled two-color SVG with no strokes, so `strokeWidth` is accepted and
 * ignored rather than silently distorting it. Cast at this one boundary
 * instead of at each call site, so a consumer just passes `HubspotNavIcon`.
 */
export const HubspotNavIcon = HubspotLogo as unknown as LucideIcon;
